import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import {findLastIndex, isArray} from "lodash";
import moment from "moment";
import React, {useCallback, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import EstimatedWeight from "../../../../beans/estimated-weight/EstimatedWeight";
import {makeGetWeightingEvents} from "../../../../selectors/animalDocumentsSelectors";
import {getOtherWeightsSelector} from "../../../../selectors/settingsSelector";
import {
    localMomentToUTC,
    utcDateFormatter,
    utcMomentToLocal,
} from "../../../../utils/DateTimeUtils";
import {
    interp,
    isBetween,
    isFiniteNumber,
    minMaxBy,
} from "../../../../utils/MathUtils";
import {convertWeightUnitTo, getUnit} from "../../../../utils/UnitUtils";
import useAnimalGroupInfo from "../useAnimalGroupInfo";
import useDomain from "../useDomain";
import ChartTemplate from "./ChartTemplate";
import {UnitTypes} from "../../../../constans/unitTypes";
import {getUnitSystem} from "../../../../utils/SettingsUtils";

const CustomTooltip = ({label, payload}) => {
    return (
        <div className="compare-insertion-tooltip recharts-default-tooltip">
            <div>{utcDateFormatter(label)}</div>
            <ul>
                {!!payload &&
                    payload.map((item, i) => {
                        // nie chcemy wyświetlać labelka
                        if (item.dataKey === "gain" && !item.strokeWidth)
                            return null;
                        return (
                            <li key={i} style={{color: item.fill}}>
                                <span>
                                    {item.name}
                                    {": "}
                                    {isArray(item.value) && (
                                        <>
                                            {item.value.join("-")}
                                            {item.unit}
                                        </>
                                    )}
                                    {!isArray(item.value) && (
                                        <>
                                            {item.value}
                                            {item.unit}
                                        </>
                                    )}
                                </span>
                            </li>
                        );
                    })}
            </ul>
        </div>
    );
};

const GainChart = () => {
    const ref = useRef(null);

    const animalUnit = useRef(UnitTypes.SMALL);

    const weightUnit = useRef(getUnit("weight", animalUnit.current));

    const groupInfo = useAnimalGroupInfo();

    const unitSystem = useMemo(() => getUnitSystem(), []);

    const weightFormatter = useCallback(
        (value) => {
            if (value === undefined || value === null || isNaN(value))
                return null;
            return convertWeightUnitTo(value, {
                showUnit: false,
                unit: animalUnit.current,
                rawValue: true,
                fixed: unitSystem === "metric" ? 0 : 2,
            });
        },
        [unitSystem]
    );

    const chartDomain = useDomain();

    const getWeightingEvents = useMemo(() => makeGetWeightingEvents(), []);

    const weightingEvents = useSelector(getWeightingEvents);

    const weightDict = useMemo(() => {
        const dictionary = {};
        // group weight reads by a day (utc)
        for (const event of weightingEvents) {
            if (
                ![EventTypes.WEIGHTING, EventTypes.INSERTION].includes(
                    event?.EvCode
                )
            )
                continue;
            const utcTIME = +localMomentToUTC(event?.EvTime).startOf("day");
            if (!dictionary[utcTIME]) dictionary[utcTIME] = [];
            dictionary[utcTIME].push({
                Weight: event?.EvData?.Weight || 0,
                AnmCnt: event?.EvData?.AnmCnt || event?.AnmCnt || 1,
            });
        }
        // transform many weights into a single one using weighted average
        for (const timestamp in dictionary) {
            if (dictionary.hasOwnProperty(timestamp)) {
                const {Weight, AnmCnt} = dictionary[timestamp].reduce(
                    (total, current) => {
                        return {
                            Weight:
                                total.Weight + current.Weight * current.AnmCnt,
                            AnmCnt: total.AnmCnt + current.AnmCnt,
                        };
                    },
                    {Weight: 0, AnmCnt: 0}
                );
                dictionary[timestamp] =
                    AnmCnt > 0 ? Math.round(Weight / AnmCnt) : 0;
            }
        }
        return dictionary;
    }, [weightingEvents]);

    const weightArray = useSelector(getOtherWeightsSelector);

    const dataSet = useMemo(() => {
        const fmt = weightFormatter;
        const ds = [];
        const [chartStart, chartEnd] = chartDomain;
        const diff = Math.abs(
            moment.utc(chartStart).diff(moment.utc(chartEnd), "day")
        );
        const estimate = new EstimatedWeight(0, []);
        estimate.setWeightTable(weightArray, 1200);
        estimate.setBirthDate(groupInfo.birthTime);
        for (let i = 0; i <= diff; i++) {
            const dayUTC = moment.utc(chartStart).add(i, "day");
            const dayLOCAL = utcMomentToLocal(dayUTC);
            const estimated = estimate.getGainByDate(+dayLOCAL);
            const isGroupActive = isBetween(
                +dayUTC,
                groupInfo.start,
                groupInfo.end
            );
            const weight = weightDict[+dayUTC] ?? null;
            if (!isGroupActive) {
                ds.push({
                    time: +dayUTC,
                    value: null,
                    details: [],
                    error: null,
                    weight,
                    estimated: fmt(estimated),
                });
                continue;
            }
            const {value, details} = (() => {
                const details = [];
                let cnt = 0;
                const locations = Object.keys(
                    groupInfo.animalNumberPerLocation
                );
                for (const locationId of locations) {
                    const animals = groupInfo.getNumberByTimeAndLocation(
                        +dayLOCAL,
                        locationId
                    );
                    if (animals === 0) continue;
                    const locationData = {
                        locationId,
                        value: animals,
                        error: null,
                        count: animals,
                    };
                    cnt += animals;
                    details.push(locationData);
                }
                return {value: cnt, details};
            })();
            const lastWeightIndex = findLastIndex(
                ds,
                ({weight}) => weight !== null
            );
            ds.push({
                time: +dayUTC,
                value,
                details,
                error: null,
                weight,
                estimated: fmt(estimated),
            });
            if (weight !== null && lastWeightIndex !== -1) {
                const currentIndex = ds.length - 1;
                for (let j = lastWeightIndex + 1; j < currentIndex; j++) {
                    const newWeight = interp.linear({
                        x: j,
                        x1: lastWeightIndex,
                        y1: ds[lastWeightIndex].weight,
                        x2: currentIndex,
                        y2: weight,
                    });
                    const item = ds[j];
                    item.weight = newWeight;
                    item.intepolated = true;
                }
            }
        }
        let hasGain = false;
        for (let i = 1; i < ds.length; i++) {
            const prev = ds[i - 1];
            const current = ds[i];
            if (!isFiniteNumber(prev?.weight)) continue;
            if (!isFiniteNumber(current?.weight)) continue;
            const item = ds[i];
            if (!current.intepolated) {
                item.gain = +(fmt(current?.weight) - fmt(prev?.weight)).toFixed(
                    2
                );
            }
            item.estimatedGain = current.estimated;
            // item.increasing = item.gain[1] >= item.gain[0];
            hasGain = true;
        }
        return hasGain ? ds : [];
    }, [groupInfo, weightFormatter, chartDomain, weightDict, weightArray]);

    const domainY = useMemo(() => {
        const [min, max] = minMaxBy(dataSet, (o) => o.gain).map(
            weightFormatter
        );
        const maxCeil500 =
            (Math.round((max % 500) / 500) + Math.ceil(max / 500)) * 500;
        return [Math.min(0, min), Math.max(1500, maxCeil500)];
    }, [dataSet, weightFormatter]);

    const ticks = useMemo(() => {
        const points = [];
        let [min, max] = domainY;
        while (max >= min) {
            points.push(max);
            max -= 100;
        }
        return points;
    }, [domainY]);

    const {t} = useTranslation();

    const dataDef = useMemo(() => {
        return [
            {
                dataKey: "estimatedGain",
                color: "blue",
                strokeOpacity: 0.75,
                strokeWidth: 4,
                strokeDasharray: "5 5",
                type: "linear",
                chartType: "Line",
                unit: weightUnit.current,
                name: t("expectedGain"),
            },
            {
                dataKey: "gain",
                color: "green",
                strokeWidth: 2,
                type: "linear",
                chartType: "Line",
                unit: weightUnit.current,
                name: t("averageGain"),
                // minPointSize: 1,
                connectNulls: true,
            },
            {
                dataKey: "gain",
                color: "green",
                strokeWidth: 0,
                type: "linear",
                chartType: "Label",
                unit: weightUnit.current,
                name: t("showLabelFor", {label: "$t(averageGain)"}),
                // minPointSize: 1,
                connectNulls: true,
            },
        ];
    }, [t]);

    const cartesianGrid = useMemo(
        () => ({
            horizontal: true,
        }),
        []
    );

    return (
        <ChartTemplate
            overrideDesktop={true}
            cartesianGrid={cartesianGrid}
            show={true}
            domain={domainY}
            ticksY={ticks}
            data={dataSet}
            error={false}
            YaxisName={t("averageWeightShort")}
            name={t("averageGain")}
            loading={false}
            id={"cy-gain-chart"}
            dataDef={dataDef}
            customTooltip={CustomTooltip}
            dataSet={dataSet}
            saveAsExcell={t("averageWeightShort")}
            defaultExpanded={true}
            forwardRef={ref}
        />
    );
};
export default React.memo(GainChart);
